import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { getFacings, createFacing, updateFacing, deleteFacing } from '../../services/master';

const Facing = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [facingData, setFacingData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  const [openModal, setOpenModal] = useState(false);
  const [editingFacing, setEditingFacing] = useState(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [newFacing, setNewFacing] = useState({
    name: '',
    code: '',
    status: 'Active',
  });

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedFacingForDelete, setSelectedFacingForDelete] = useState(null);

  const fetchFacings = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseURL}/api/globalMasterDetails?type=facing`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Facings: ${response.statusText}`);
      }
      const data = await response.json();
      const facings = Array.isArray(data.data)
        ? data.data.map((item) => ({
          id: item._id,
          name: item.details?.facingName || 'Unknown',
          code: item.details?.facingCode || '',
          status: item.status === true ? 'Active' : 'Inactive',
          rawStatus: item.status,
        }))
        : [];
      setFacingData(facings);
    } catch (err) {
      setError(err.message);
      setFacingData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFacings();
  }, []);

  const getFilteredFacings = () => {
    if (!Array.isArray(facingData)) {
      return [];
    }
    return facingData.filter((facing) => {
      const matchesSearch =
        facing.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        facing.code?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || facing.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Inactive':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newFacing.name.trim()) newErrors.name = 'Facing name is required';
    if (!newFacing.code.trim()) newErrors.code = 'Facing code is required';
    return newErrors;
  };

  const handleEdit = (facing) => {
    setEditingFacing(facing);
    setNewFacing({
      name: facing.name || '',
      code: facing.code || '',
      status: facing.rawStatus === true || facing.status === 'Active' ? 'Active' : 'Inactive',
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (facing) => {
    setSelectedFacingForDelete(facing);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedFacingForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseURL}/api/globalMasterDetails/${selectedFacingForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'Facing deleted successfully!' });
      setAlertOpen(true);
      fetchFacings(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete facing.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedFacingForDelete(null);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    setErrors({});
    fetchFacings(true);
  };

  const handleOpenModal = () => {
    setEditingFacing(null);
    setNewFacing({
      name: '',
      code: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingFacing(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFacing({ ...newFacing, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleAddFacing = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoadingModal(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const payload = {
        type: 'facing',
        details: {
          facingName: newFacing.name.trim(),
          facingCode: newFacing.code.trim(),
        },
        status: newFacing.status === 'Active',
      };

      const url = editingFacing
        ? `${baseURL}/api/globalMasterDetails/${editingFacing.id}`
        : `${baseURL}/api/globalMasterDetails`;
      const method = editingFacing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to ${editingFacing ? 'update' : 'add'} facing: ${response.statusText}`
        );
      }

      setAlertConfig({
        title: 'Success',
        message: editingFacing ? 'Facing updated successfully!' : 'Facing added successfully!'
      });
      setAlertOpen(true);
      await fetchFacings(true);

      handleCloseModal();
    } catch (error) {
      setErrors({ general: error.message || 'An error occurred. Please try again.' });
    } finally {
      setIsLoadingModal(false);
    }
  };

  const filteredFacings = getFilteredFacings();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Facing Master Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchFacings()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenModal}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                flex: { xs: 1, sm: 'none' },
                textTransform: 'none'
              }}
            >
              Add New Facing
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the facing <strong>{selectedFacingForDelete?.name}</strong>? This action cannot be undone.
      </ConfirmModal>

      {/* Search and Filter Section */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Facings..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setPage(0);
                  const count = getFilteredFacings().length;
                  setAlertConfig({
                    title: 'Search Results',
                    message: count > 0 ? `Found ${count} facings matching your criteria.` : 'No facings found matching your criteria.'
                  });
                  setAlertOpen(true);
                }}
                startIcon={<SearchIcon />}
                sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleReset}
                sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table Section */}
      {!isLoading && (
        <Card sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Facing Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFacings.length > 0 ? (
                  filteredFacings
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((facing, index) => (
                      <TableRow key={facing.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{facing.name || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={facing.code || '-'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={facing.status || 'N/A'}
                            size="small"
                            color={facing.status === 'Active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {canWrite && (
                              <Tooltip title="Edit Facing">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEdit(facing)}
                                  disabled={isLoading}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canWrite && (
                              <Tooltip title="Delete Facing">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteClick(facing)}
                                  disabled={isLoading}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No facings found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredFacings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Add/Edit Facing Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          {editingFacing ? 'Edit Facing' : 'Add New Facing'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facing Name"
                name="name"
                value={newFacing.name}
                onChange={(e) => {
                  const inputName = e.target.value;
                  const code = abbreviate(inputName);
                  setNewFacing(prev => ({ ...prev, name: inputName, code }));
                }}
                error={!!errors.name}
                helperText={errors.name || 'e.g., North, South, East, North-East, etc.'}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facing Code"
                name="code"
                value={newFacing.code}
                onChange={handleInputChange}
                error={!!errors.code}
                helperText={errors.code}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={newFacing.status}
                onChange={handleInputChange}
                margin="normal"
                SelectProps={{
                  native: true,
                }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </TextField>
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={isLoadingModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddFacing}
            disabled={isLoadingModal}
            startIcon={isLoadingModal ? <CircularProgress size={20} /> : null}
          >
            {isLoadingModal ? 'Saving...' : editingFacing ? 'Update Facing' : 'Add Facing'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function abbreviate(direction) {
  switch (direction) {
    case 'North': return 'N';
    case 'South': return 'S';
    case 'East': return 'E';
    case 'West': return 'W';
    case 'North-East': return 'NE';
    case 'North-West': return 'NW';
    case 'South-East': return 'SE';
    case 'South-West': return 'SW';
    default: return '';
  }
}

export default Facing;